import { useState, useMemo } from 'react';

export type SortDirection = 'asc' | 'desc';

export interface SortConfig<T> {
  /** Campo su cui ordinare */
  field: keyof T;
  /** Direzione di ordinamento */
  direction: SortDirection;
}

interface UseSortOptions<T> {
  /** Campo iniziale per l'ordinamento */
  initialField?: keyof T;
  /** Direzione iniziale di ordinamento */
  initialDirection?: SortDirection;
}

interface UseSortReturn<T> {
  /** Dati ordinati */
  sortedData: T[];
  /** Configurazione corrente di ordinamento */
  sortConfig: SortConfig<T>;
  /** Funzione per gestire l'ordinamento su un campo */
  handleSort: (field: keyof T) => void;
  /** Funzione per impostare manualmente l'ordinamento */
  setSortConfig: (config: SortConfig<T>) => void;
}

/**
 * Funzione di comparazione per ordinamento
 * Gestisce string, number, boolean e Date
 */
const compareValues = (aValue: unknown, bValue: unknown, direction: SortDirection): number => {
  // Gestione valori null/undefined
  if (aValue == null && bValue == null) return 0;
  if (aValue == null) return direction === 'asc' ? -1 : 1;
  if (bValue == null) return direction === 'asc' ? 1 : -1;

  // Conversione per confronto
  let a: string | number | Date = aValue as string | number | Date;
  let b: string | number | Date = bValue as string | number | Date;

  // Gestione Date
  if (a instanceof Date && b instanceof Date) {
    const result = a.getTime() - b.getTime();
    return direction === 'asc' ? result : -result;
  }

  // Gestione numeri
  if (typeof a === 'number' && typeof b === 'number') {
    const result = a - b;
    return direction === 'asc' ? result : -result;
  }

  // Gestione boolean
  if (typeof a === 'boolean' && typeof b === 'boolean') {
    const result = Number(a) - Number(b);
    return direction === 'asc' ? result : -result;
  }

  // Gestione stringhe (default)
  const aStr = String(a).toLowerCase();
  const bStr = String(b).toLowerCase();
  
  const result = aStr.localeCompare(bStr, 'it', { 
    sensitivity: 'base',
    numeric: true 
  });
  
  return direction === 'asc' ? result : -result;
};

/**
 * Hook personalizzato per gestire l'ordinamento di array di dati
 * @param data - Array di dati da ordinare
 * @param options - Opzioni di configurazione
 * @returns Oggetto con dati ordinati e funzioni per gestire l'ordinamento
 */
export const useSort = <T extends Record<string, any>>(
  data: T[],
  options: UseSortOptions<T> = {}
): UseSortReturn<T> => {
  const {
    initialField,
    initialDirection = 'asc'
  } = options;

  // Determina il campo iniziale se non specificato
  const defaultField = initialField || (data.length > 0 ? Object.keys(data[0])[0] as keyof T : '' as keyof T);

  const [sortConfig, setSortConfigState] = useState<SortConfig<T>>({
    field: defaultField,
    direction: initialDirection
  });

  // Calcola dati ordinati
  const sortedData = useMemo((): T[] => {
    if (!sortConfig.field || data.length === 0) {
      return [...data];
    }

    return [...data].sort((a, b) => {
      const aValue = a[sortConfig.field];
      const bValue = b[sortConfig.field];
      
      return compareValues(aValue, bValue, sortConfig.direction);
    });
  }, [data, sortConfig]);

  // Gestisce il cambio di ordinamento su un campo
  const handleSort = (field: keyof T) => {
    setSortConfigState(prev => {
      // Se stesso campo, inverti direzione
      if (prev.field === field) {
        return {
          field,
          direction: prev.direction === 'asc' ? 'desc' : 'asc'
        };
      }
      
      // Nuovo campo, inizia con 'asc'
      return {
        field,
        direction: 'asc'
      };
    });
  };

  // Imposta configurazione manualmente
  const setSortConfig = (config: SortConfig<T>) => {
    setSortConfigState(config);
  };

  return {
    sortedData,
    sortConfig,
    handleSort,
    setSortConfig
  };
};

export default useSort;